import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)] p-4 animate-in">
        <div className="mb-8 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
        </div>
        <Card className="w-full max-w-sm shadow-lg border-0 ring-1 ring-[var(--color-border)] text-center">
          <CardHeader className="space-y-1 pb-4">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle className="h-6 w-6" />
            </div>
            <CardTitle className="text-center text-2xl font-bold tracking-tight">Check your email</CardTitle>
            <CardDescription className="text-center">If the email exists, a reset link has been sent.</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login" className="inline-flex items-center text-sm text-[var(--color-primary)] hover:underline"><ArrowLeft className="mr-1 h-4 w-4" /> Back to login</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)] p-4 animate-in">
      <div className="mb-8 flex items-center justify-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
      </div>
      <Card className="w-full max-w-sm shadow-lg border-0 ring-1 ring-[var(--color-border)]">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-center text-2xl font-bold tracking-tight">Jani</CardTitle>
          <CardDescription className="text-center">Reset your password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Badge variant="destructive" className="w-full justify-center py-1.5 text-xs font-medium rounded-md">{error}</Badge>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="pl-10" placeholder="name@company.com" required />
              </div>
            </div>
            <Button data-testid="forgot-submit-button" type="submit" disabled={loading} loading={loading} className="w-full">Send Reset Link</Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <Link to="/login" className="text-[var(--color-primary)] hover:underline inline-flex items-center"><ArrowLeft className="mr-1 h-4 w-4" /> Back to login</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
